"use client";

import { safeLocalStorageSetItem } from "@/util/safeLocalStorage";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { GradientCornerBorder } from "./GradientCornerBorder";

export const HOME_GUIDE_COMPLETED_KEY = "vidmuse_home_guide_completed";

interface SpotlightState {
  rect: DOMRect | null;
  margin: number;
  rounded: string;
  popover?: ReactNode;
  gradientWidth?: number;
  gradientHeight?: number;
}

interface SpotlightContextType {
  activeStep: number;
  setStep: (step: number) => void;
  registerTarget: (step: number, state: SpotlightState) => void;
  unregisterTarget: (step: number) => void;
  completeGuide: () => void;
}

const SpotlightContext = createContext<SpotlightContextType | null>(null);

export const useSpotlight = () => {
  const context = useContext(SpotlightContext);
  if (!context)
    throw new Error("useSpotlight must be used within a SpotlightProvider");
  return context;
};

interface SpotlightProviderProps {
  children: ReactNode;
  initialStep?: number;
  overlayOpacity?: number;
}

export function SpotlightProvider({ children }: { children: ReactNode }) {
  const [activeStep, setActiveStep] = useState(0); // 0 means guide is hidden
  const [targets, setTargets] = useState<Record<number, SpotlightState>>({});
  const [overlayOpacity] = useState(0.85);

  const registerTarget = useCallback((step: number, state: SpotlightState) => {
    setTargets((prev) => ({ ...prev, [step]: state }));
  }, []);

  const unregisterTarget = useCallback((step: number) => {
    setTargets((prev) => {
      const { [step]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const completeGuide = useCallback(() => {
    safeLocalStorageSetItem(HOME_GUIDE_COMPLETED_KEY, "true");
    setActiveStep(0);
  }, []);

  const currentTarget = targets[activeStep];
  const rect = currentTarget?.rect;
  const margin = currentTarget?.margin ?? 20;
  const rounded = currentTarget?.rounded ?? "16px";
  const popover = currentTarget?.popover;
  const gradientWidth = currentTarget?.gradientWidth;
  const gradientHeight = currentTarget?.gradientHeight;

  const borderRadiusValue = parseInt(rounded) || 16;

  // Disable scroll when spotlight is active
  useEffect(() => {
    if (activeStep > 0) {
      // For step 1, scroll to top first before locking scroll
      // This ensures the spotlight positions are calculated correctly
      if (activeStep === 1) {
        window.scrollTo({ top: 0, behavior: "instant" });
      }

      // Then lock the scroll
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeStep]);

  return (
    <SpotlightContext.Provider
      value={{
        activeStep,
        setStep: setActiveStep,
        registerTarget,
        unregisterTarget,
        completeGuide,
      }}
    >
      {children}
      {/* Overlay layer - Full screen to block interactions */}
      {activeStep > 0 && rect && (
        <div className="fixed inset-0 z-[100] pointer-events-auto transition-opacity duration-300">
          {/* The Highlight Hole */}
          <div
            className="absolute transition-all duration-300 ease-in-out"
            style={{
              top: rect.top - margin,
              left: rect.left - margin,
              width: rect.width + margin * 2,
              height: rect.height + margin * 2,
              borderRadius: rounded,
              boxShadow: `0 0 0 9999px rgba(0, 0, 0, ${overlayOpacity})`,
            }}
          >
            <GradientCornerBorder
              borderRadius={borderRadiusValue}
              width={gradientWidth}
              height={gradientHeight}
            />
          </div>
        </div>
      )}
      {/* Popover layer - rendered separately to ensure it's on top and potentially interactive */}
      {activeStep > 0 && rect && popover && (
        <div
          className="fixed z-[101] transition-all duration-300 ease-in-out pointer-events-none"
          style={{
            top: rect.top - margin,
            left: rect.left - margin,
            width: rect.width + margin * 2,
            height: rect.height + margin * 2,
          }}
        >
          {popover}
        </div>
      )}
    </SpotlightContext.Provider>
  );
}
